import { Box, Tab, tabClasses, TabList } from "@mui/joy";
import { useEffect, useRef } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import TabContent from "./tab/TabContent";
import { taskSectionAtom } from "recoil/taskSectionAtom";
import { currentTaskSectionIndexAtom } from "recoil/currentTaskSectionIndexAtom";
import { currentDragTaskAtom } from "recoil/currentDragTaskAtom ";
import { transferTaskToNewSection } from "./logics";
import { layoutAtom } from "recoil/layoutAtom";

export default function TaskTabs({ index }: { index: number }) {
  const tabRefs = useRef<HTMLElement[]>([]);
  const [taskSectionState, setTaskSectionState] =
    useRecoilState(taskSectionAtom);
  const { openTaskKeyList, currentTaskKey } = (taskSectionState || [])[index];

  const [currentDragTask, setCurrentDragTask] =
    useRecoilState(currentDragTaskAtom);
  const [currentTaskSectionIndex, setCurrentTaskSectionIndex] = useRecoilState(
    currentTaskSectionIndexAtom
  );

  useEffect(() => {
    const currentTaskIndex = openTaskKeyList.findIndex(
      (taskKey) => taskKey === currentTaskKey
    );
    if (currentTaskKey && tabRefs.current[currentTaskIndex]) {
      tabRefs.current[currentTaskIndex].scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [currentTaskKey, openTaskKeyList]);

  useEffect(() => {
    if (
      taskSectionState?.find((element) => element.openTaskKeyList.length === 0)
    ) {
      setTaskSectionState((taskSectionState) =>
        taskSectionState?.filter(
          (element) => element.openTaskKeyList.length > 0
        )
      );
      setCurrentTaskSectionIndex(
        taskSectionState?.filter(
          (element) => element.openTaskKeyList.length > 0
        ).length - 1
      );
    }
  }, [setCurrentTaskSectionIndex, setTaskSectionState, taskSectionState]);

  const dropInTabarea = (target: HTMLElement) => {
    if (taskSectionState && currentDragTask) {
      const dropTaskKey = target.innerText;
      const insertIndex = taskSectionState[index].openTaskKeyList.findIndex(
        (taskKey) => taskKey === dropTaskKey
      );

      transferTaskToNewSection(
        currentDragTask.taskKey,
        taskSectionState,
        setTaskSectionState,
        setCurrentTaskSectionIndex,
        currentDragTask.taskSectionIndex,
        index,
        insertIndex === -1
          ? taskSectionState[index].openTaskKeyList.length
          : insertIndex
      );
    }
    setCurrentDragTask(undefined);
  };

  const showDropZone = (target: HTMLElement) => {
    (target as HTMLElement).style.backgroundColor = "rgba(0,0,0,0.1)";
  };
  const hideDropZone = (target: HTMLElement) => {
    (target as HTMLElement).style.backgroundColor = "transparent";
  };
  const { isLeftSideBarOpen, isRightSideBarOpen } = useRecoilValue(layoutAtom);

  return (
    <Box
      flexGrow={1}
      sx={{
        flex: 1,
        backgroundColor: "background.level3",
        minWidth: 0,
        overflow: "hidden",
        height: "100%",
      }}
      onDrop={(e) => {
        hideDropZone(e.target as HTMLElement);
        dropInTabarea(e.target as HTMLElement);
      }}
      onDragOver={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onDragEnter={(e) => {
        if (currentDragTask) {
          showDropZone(e.target as HTMLElement);
        }
      }}
      onDragLeave={(e) => {
        hideDropZone(e.target as HTMLElement);
      }}
    >
      <TabList
        sx={{
          p: 0,
          pl: index === 0 && !isLeftSideBarOpen ? "64px" : 0,
          pr:
            index === (taskSectionState || []).length - 1 && !isRightSideBarOpen
              ? "64px"
              : 0,
          width: "100%",
          height: "100%",
          "--ListItem-minHeight": 0,
          overflow: "auto",
          [`& .${tabClasses.root}`]: {
            fontSize: "sm",
            fontWeight: "lg",
            [`&[aria-selected="true"]`]: {
              bgcolor: "background.level1",
            },
            [`&[aria-selected="false"]`]: {
              opacity: 0.5,
            },
            [`&.${tabClasses.focusVisible}`]: {
              outlineOffset: "-4px",
            },
          },
        }}
        className="scrollbar-hide"
        disableUnderline
      >
        {openTaskKeyList.map((taskKey, taskIndex) => (
          <Tab
            ref={(el) =>
              currentTaskSectionIndex === index &&
              (tabRefs.current[taskIndex] = el as HTMLElement)
            }
            className="tab"
            key={`tab${taskKey}`}
            value={taskKey}
            sx={{
              position: "relative",
              p: 0,

              width: "fit-content",
              flexShrink: 0,
              border: 0,
              borderRight: 1,
              borderColor: "background.level3",
            }}
            disableIndicator
            draggable
            onDragStart={() => {
              setCurrentDragTask({
                taskKey: taskKey,
                taskSectionIndex: index,
              });
            }}
            onDragEnd={() => {
              setCurrentDragTask(undefined);
            }}
          >
            <div
              className={`absolute w-full h-full z-[100] text-transparent ${
                !currentDragTask && " pointer-events-none"
              }`}
            >
              {taskKey}
            </div>
            <TabContent key={`tab${taskKey}`} index={index} taskKey={taskKey} />
          </Tab>
        ))}
        {/* <div className="flex-1 h-full "></div> */}
      </TabList>
    </Box>
  );
}
